import React, { useState } from "react";
import { doc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../services/firebase";
import Swal from "sweetalert2";
import ReCAPTCHA from "react-google-recaptcha";
import {
  isMaintenanceMode,
  MAINTENANCE_ACTION_BLOCK_TEXT,
} from "../utils/maintenance";
import AsSeenOn from "./AsSeenOn/AsSeenOn";
import Award from "./Award/Award";

// NOTE: this form deliberately does not collect phone numbers or SMS consent.
// The LeadConnector chat widget (see components/ChatWidget.js) is the only
// SMS opt-in on the pages where it is embedded, as carriers require.
const Booking = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    mediaUrl: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [recaptchaValue, setRecaptchaValue] = useState("");
  const recaptchaRef = React.useRef();

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Validate email format
    if (name === "email") {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailError(emailPattern.test(value) ? "" : "Invalid email format");
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (10 MB = 10485760 bytes)
      if (file.size > 10485760) {
        Swal.fire({
          icon: "error",
          title: "File Too Large",
          text: "Please select a file smaller than 10 MB.",
          showConfirmButton: true,
        });
        // Clear the input
        event.target.value = "";
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Block booking submissions during maintenance mode
    if (isMaintenanceMode()) {
      Swal.fire({
        icon: "info",
        title: "Maintenance Mode",
        text: MAINTENANCE_ACTION_BLOCK_TEXT,
        showConfirmButton: true,
      });
      return;
    }

    if (emailError) {
      Swal.fire({
        icon: "error",
        title: "Invalid Email",
        text: emailError,
        showConfirmButton: true,
      });
      return;
    }
    setIsLoading(true);

    let mediaUrl = "";

    if (selectedFile) {
      const timestamp = new Date(
        new Date().toLocaleString("en-US", { timeZone: "America/Chicago" })
      ).getTime();
      const storageRef = ref(
        storage,
        `/media/${timestamp}_${selectedFile.name}`
      );
      try {
        await uploadBytes(storageRef, selectedFile);
        mediaUrl = await getDownloadURL(storageRef);
      } catch (error) {
        console.error("Error uploading file: ", error);
      }
    }

    const consultationData = {
      name: formData.name,
      email: formData.email,
      message: formData.message,
      mediaUrl,
      createdAt: new Date(
        new Date().toLocaleString("en-US", { timeZone: "America/Chicago" })
      ),
      completed: false,
    };

    try {
      const timestamp = new Date(
        new Date().toLocaleString("en-US", { timeZone: "America/Chicago" })
      )
        .getTime()
        .toString();
      await setDoc(doc(db, "consultations", timestamp), consultationData);
      await setDoc(doc(db, "mail", timestamp), {
        to: "roselynn@cpnmail.com",
        //to: "dev.mohamed.yasser@gmail.com",
        message: {
          subject: `New consultation from ${formData.name}`,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #333;">Hello Roselynn,</h2>
              
              <p style="color: #666;">You got a new message consultation:</p>

              <div style="background: #f5f5f5; padding: 20px; border-radius: 5px; margin: 15px 0;">
                <p style="margin: 10px 0;"><strong>Full Name:</strong> ${
                  formData.name
                }</p>
                <p style="margin: 10px 0;"><strong>Email:</strong> ${
                  formData.email
                }</p>
                <p style="margin: 10px 0;"><strong>Message:</strong> ${
                  formData.message
                }</p>
                <p style="margin: 10px 0;"><strong>File:</strong> ${
                  mediaUrl
                    ? `<a href="${mediaUrl}" target="_blank" style="color: #007bff; text-decoration: none;">View Attachment</a>`
                    : "No file attached"
                }</p>
                <p style="margin: 10px 0;"><strong>Date:</strong> ${new Date().toLocaleString(
                  "en-US",
                  { timeZone: "America/Chicago" }
                )}</p>
              </div>

              <p style="color: #666; margin-top: 20px;">Best wishes,<br>Mohamed Yasser</p>
            </div>
          `,
        },
      });

      Swal.fire({
        icon: "success",
        title: "Consultation Sent Successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      setFormData({
        name: "",
        email: "",
        message: "",
        mediaUrl: "",
      });
      setSelectedFile(null);
      setRecaptchaValue("");
      document.getElementById("media").value = "";
      recaptchaRef.current.reset();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Something Went Wrong",
        showConfirmButton: false,
        timer: 1500,
      });
      console.error("Error submitting consultation: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  function gtag_report_conversion() {
    window.gtag("event", "conversion", {
      send_to: "AW-16818208218/hZinCJ6b5fUZENr7xdM-",
      value: 1.0,
      currency: "USD",
    });
    return false;
  }

  return (
    <div id="booking" style={{ padding: "20px" }}>
      <Award />
      <AsSeenOn />
      <div className="form">
        <div className="form-section">
          <h1 className="form-heading">REQUEST A FREE CONSULTATION NOW!</h1>
        </div>

        <div className="form-content">
          <form id="myform" className="form-animate" onSubmit={handleSubmit}>
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              name="name"
              type="text"
              placeholder="Enter your full name"
              className="form-control"
              value={formData.name}
              onChange={handleChange}
              disabled={isMaintenanceMode()}
              required
            />
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="Enter your email"
              className="form-control"
              value={formData.email}
              onChange={handleChange}
              disabled={isMaintenanceMode()}
              required
            />
            {emailError && (
              <div className="error-message text-danger">{emailError}</div>
            )}
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              placeholder="Enter your message"
              className="form-control"
              value={formData.message}
              onChange={handleChange}
              disabled={isMaintenanceMode()}
              required
            />
            <label htmlFor="media">Attach Files</label>
            <input
              type="file"
              id="media"
              placeholder="Attach Your File"
              accept="image/*, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              style={{ display: "block" }}
              onChange={handleFileChange}
              disabled={isMaintenanceMode()}
            />

            <p className="text-muted mt-3" style={{ fontSize: "0.9rem" }}>
              Prefer a text message? Use the chat widget in the corner of this
              page &mdash; it is the only place we collect phone numbers and SMS
              consent. See our{" "}
              <a
                href="/PrivacyPolicy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              .
            </p>

            <ReCAPTCHA
              ref={recaptchaRef}
              className="m-3"
              sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
              onChange={(val) => setRecaptchaValue(val)}
              disabled={isMaintenanceMode()}
            />
            <div style={{ textAlign: "center" }}>
              <button
                id="submit"
                type="submit"
                className="form-submit"
                disabled={!recaptchaValue || isLoading || isMaintenanceMode()}
                onClick={() => gtag_report_conversion()}
              >
                {isLoading ? "Sending..." : "Send Now"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Booking;
